import { ScaleElement } from '@scale/base-element';

class ScaleSearchOverlay extends ScaleElement {
  #cache = new Map();
  #fetchController;

  setup() {
    const input = this.ref('search-input');
    if (!input) return;

    const predictive = !this.hasAttribute('data-no-predictive');

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#close();
        return;
      }

      if (!predictive) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.#navigateResults(1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.#navigateResults(-1);
          break;
        case 'Enter': {
          const focused = this.ref('results')?.querySelector('.is-focused a');
          if (focused) {
            e.preventDefault();
            focused.click();
          }
          break;
        }
      }
    }, { signal: this.signal });

    if (!predictive) return;

    input.addEventListener('input', () => {
      this.debounce('search', () => this.#onInput(), 300);
    }, { signal: this.signal });
  }

  openSearch() {
    this.hidden = false;
    this.ref('search-input')?.focus();
  }

  closeSearch() {
    this.#close();
  }

  clearSearch() {
    const input = this.ref('search-input');
    const results = this.ref('results');
    const clearBtn = this.ref('clear-btn');

    if (input) {
      input.value = '';
      input.focus();
    }
    if (results) results.innerHTML = '';
    if (clearBtn) clearBtn.hidden = true;

    input?.setAttribute('aria-expanded', 'false');
  }

  #close() {
    this.hidden = true;
    const input = this.ref('search-input');
    if (input) {
      input.value = '';
      input.setAttribute('aria-expanded', 'false');
    }
    const results = this.ref('results');
    if (results) results.innerHTML = '';
  }

  async #onInput() {
    const input = this.ref('search-input');
    const query = input?.value.trim();
    const clearBtn = this.ref('clear-btn');

    if (clearBtn) clearBtn.hidden = !query;

    if (!query || query.length < 2) {
      this.ref('results').innerHTML = '';
      input?.setAttribute('aria-expanded', 'false');
      return;
    }

    const cached = this.#cache.get(query);
    if (cached) {
      this.#renderResults(cached);
      return;
    }

    const locale = window.Shopify?.routes?.root || '/';
    const url = `${locale}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,collection,page,article,query&resources[limit]=4`;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#fetchController.signal });
      if (!response.ok) return;
      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('#predictive-search-results')?.innerHTML || '';

      this.#cache.set(query, content);
      if (this.#cache.size > 20) {
        const oldest = this.#cache.keys().next().value;
        this.#cache.delete(oldest);
      }

      if (input?.value.trim() === query) {
        this.#renderResults(content);
      }
    } catch { /* network error — fail silently */ }
  }

  #navigateResults(direction) {
    const results = this.ref('results');
    if (!results) return;

    const items = [...results.querySelectorAll('[role="option"]')];
    if (!items.length) return;

    const current = items.findIndex((el) => el.classList.contains('is-focused'));
    const next = Math.max(0, Math.min(items.length - 1, current + direction));

    items.forEach((el) => el.classList.remove('is-focused'));
    items[next].classList.add('is-focused');
    items[next].scrollIntoView({ block: 'nearest' });

    this.ref('search-input')?.setAttribute('aria-activedescendant', items[next].id);
  }

  #renderResults(html) {
    const results = this.ref('results');
    const input = this.ref('search-input');
    if (results) results.innerHTML = html;
    input?.setAttribute('aria-expanded', html ? 'true' : 'false');
    input?.removeAttribute('aria-activedescendant');
  }
}

if (!customElements.get('scale-search-overlay')) {
  customElements.define('scale-search-overlay', ScaleSearchOverlay);
}
